import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../api';
import { useAuth } from '../AuthContext';

const fmtHM = (sec) => { sec = Math.max(0, sec || 0); return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`; };
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—';

const card = { background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 12, padding: 20 };
const statCard = { ...card, flex: 1, minWidth: 150 };

function Stat({ label, value, sub, hint }) {
  return (
    <div style={statCard}>
      <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', margin: '6px 0 2px' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{sub}</div>}
      {hint && <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

export default function MyWork() {
  const { user } = useAuth();
  const [range, setRange] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isStaff = user?.role === 'admin' || user?.role === 'manager';

  const empId = user?.employee_id || user?.employee?.id || null;

  const load = useCallback(async () => {
    // Admins have no employee profile — don't fire a request that 400s.
    if (!empId) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (!user?.employee_id) params.set('employee_id', empId);
      const { data } = await api.get(`/my-work?${params}`);
      setData(data);
    } catch (e) { setData(null); }
    setLoading(false);
  }, [range, user, empId]);

  useEffect(() => { load(); }, [load]);

  const name = user?.employee?.name || user?.username || 'You';
  const chartData = (data?.activities || []).map(a => ({
    day: new Date(a.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' }),
    hours: +(a.seconds / 3600).toFixed(2),
  }));

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 22 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0 }}>My Work</h1>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 2 }}>{name}{data?.is_active ? ' · 🟢 working' : data?.on_break ? ' · 🟡 on break' : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <a href="/app/" onClick={(e) => { e.preventDefault(); window.__setPage && window.__setPage('downloads'); }}
            style={{ background: '#16a34a', color: '#fff', padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
            ⬇ Download Desktop App
          </a>
          <select value={range} onChange={e => setRange(e.target.value)} style={{ padding: '8px 12px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: 13 }}>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>
      </div>

      {loading && <p style={{ color: 'var(--muted)' }}>Loading…</p>}

      {!loading && !data && !empId && (
        <div style={{ ...card, textAlign: 'center', color: 'var(--muted)', padding: 40 }}>
          My Work shows your own tracked time. As an admin you don't have an employee profile —
          use <strong>Dashboard</strong> for team stats or <strong>Employee Apps</strong> for per-person usage.
        </div>
      )}
      {!loading && !data && empId && (
        <div style={{ ...card, textAlign: 'center', color: 'var(--muted)', padding: 40 }}>
          No data yet. Install the desktop app and clock in to start tracking.
        </div>
      )}

      {!loading && data && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <Stat label="Total Worked Hours" value={fmtHM(data.worked_seconds)} sub="Clocked-in (excl. breaks)" />
            <Stat label="Active Time" value={fmtHM(data.active_seconds)} sub="Actively engaging" />
            <Stat label="Productive Apps %" value={data.productive_pct == null ? '—' : `${data.productive_pct}%`} sub="In apps marked productive" />
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
            <Stat label="Idle Time %" value={`${data.idle_pct || 0}%`} sub="No system activity" />
            <Stat label="Clock-in Time" value={fmtTime(data.clock_in)} sub="First clock-in today" />
            <Stat label="Clock-out Time" value={fmtTime(data.clock_out)} sub="Last clock-out today" />
          </div>

          <div style={{ ...card }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>Activities</div>
            {chartData.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20, textAlign: 'center' }}>No activity in this period.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: 'var(--muted)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--muted)' }} axisLine={false} tickLine={false} unit="h" />
                  <Tooltip formatter={(v) => [`${v}h`, 'Worked']} cursor={{ fill: 'var(--hairline)' }} />
                  <Bar dataKey="hours" fill="#4f46e5" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
